import { Injectable } from '@angular/core';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators'; 
import { RoleApplicationMessages } from '@app/domain/role_application/locale/role-application.messages';
import { RoleApplicationAPIService } from '../api/role-application-api.service';
import { RoleApplication } from '../model/role-application.model';
import { RoleApplicationActivity } from '../model/role-application-activity.model';
import { RoleApplicationQuestion } from '../model/role-application-question.model';
import { UpdateRoleApplicationQuestion } from '../model/update-role-application-question.model';
import { RoleApplicationState } from '../model/role-application-state.model';
import { RoleApplicationUpdateReviewers } from '../model/role-application-update-reviewers.model';
import { RoleApplicationAddPrimaryReviewer } from '../model/role-application-add-primary-reviewer.model';
import { RoleApplicationSettings } from '../model/role-application-settings.model';
import { ParentFilter } from '@app/models/api/parent-filter.model';
import { Page } from '@app/models/api/page.model';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

// The single-slot settings write has no id, so it is keyed by a constant.
const SETTINGS_WRITE_KEY = 'settings';

@Injectable({
  providedIn: 'root'
})
export class RoleApplicationBusinessService {

  // key -> the write currently in flight for that application. A duplicate call while one is in
  // flight is coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  private static _updating: InFlightRegistry;
  // Single-slot guard for the company-wide settings write, keyed by SETTINGS_WRITE_KEY.
  private static _updatingSettings: InFlightRegistry;

  constructor(
    private roleApplicationAPI: RoleApplicationAPIService,
    private notifyUtils: NotifyUtils
  ) {
    RoleApplicationBusinessService._updating = new InFlightRegistry();
    RoleApplicationBusinessService._updatingSettings = new InFlightRegistry();
  }

  // Read by role-application-settings.component.html to disable/spin the save button.
  get updatingSettings(): boolean {
    return RoleApplicationBusinessService._updatingSettings.has(SETTINGS_WRITE_KEY);
  }

  get(
    ids: number[],
    applicantIds: number[],
    reviewerIds: number[],
    states: RoleApplicationState[],
    includeArchived: boolean
  ): Observable<RoleApplication[]> {
    return this.roleApplicationAPI.get(ids, applicantIds, reviewerIds, states, includeArchived);
  }

  // getPaginated(
  //   pagingParams: PagingParams,
  //   sortingParams: SortingParams,
  //   ids: number[],
  //   applicantIds: number[],
  //   reviewerIds: number[],
  //   states: RoleApplicationState[]
  // ): Observable<Page<RoleApplication>> {
  //   return this.roleApplicationAPI.getPaginated(pagingParams, sortingParams, ids, applicantIds, reviewerIds, states);
  // }

  create(roleId: number): Promise<RoleApplication> {
    return firstValueFrom(this.roleApplicationAPI.create(roleId))
      .then(res => {
        this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_CREATE_APPLICATION);
        return res;
      });
  }

  delete(id: number): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.delete(id))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_DELETE);
          return res;
        })
    );
  }

  discard(id: number): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.discard(id))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_DISCARD);
          return res;
        })
    );
  }

  submit(id: number): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.submit(id))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_SUBMIT);
          return res;
        })
    );
  }

  decline(id: number, declineReason: string): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.decline(id, declineReason))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_REJECT);
          return res;
        })
    );
  }

  approve(id: number): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.approve(id))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_FINALIZE);
          return res;
        })
    );
  }

  reopen(id: number, reopenReason: string): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.reopen(id, reopenReason))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_REJECT);
          return res;
        })
    );
  }

  award(id: number, sendBackToManager: boolean): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.award(id, sendBackToManager))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_FINALIZE);
          return res;
        })
    );
  }

  awardApprovedByAdmin(id: number): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.awardApprovedByAdmin(id))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_FINALIZE_APPROVED);
          return res;
        })
    );
  }

  updateReviewers(id: number, roleApplicationAddReviewers: RoleApplicationUpdateReviewers): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.updateReviewers(id, roleApplicationAddReviewers))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_ADD_REVIEWERS);
          return res;
        })
    );
  }

  addPrimaryReviewer(id: number, roleApplicationAddPrimaryReviewer: RoleApplicationAddPrimaryReviewer): Promise<RoleApplication> {
    return RoleApplicationBusinessService._updating.run(id, () =>
      firstValueFrom(this.roleApplicationAPI.updatePrimaryReviewer(id, roleApplicationAddPrimaryReviewer))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_ADD_PRIMARY_REVIEWER);
          return res;
        })
    );
  }

  getActivityForApplicationId(applicationId: number): Observable<RoleApplicationActivity[]> {
    return this.roleApplicationAPI.getActivityForApplicationId(applicationId);
  }

  getApplicantQuestions(applicationId: number): Observable<RoleApplicationQuestion[]> {
    return this.roleApplicationAPI.getApplicantQuestions(applicationId)
      .pipe(
        map(questions => questions.filter(q => q.associatedTemplateQuestion !== null))
      );
  }

  getPrimaryReviewerQuestions(applicationId: number): Observable<RoleApplicationQuestion[]> {
    return this.roleApplicationAPI.getPrimaryReviewerQuestions(applicationId)
      .pipe(
        map(questions => questions.filter(q => q.associatedTemplateQuestion !== null))
      );
  }

  updateApplicantQuestions(applicationId: number, updatedQuestions: UpdateRoleApplicationQuestion[]) {
    return RoleApplicationBusinessService._updating.run(applicationId, () =>
      firstValueFrom(this.roleApplicationAPI.updateApplicantQuestions(applicationId, updatedQuestions))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_ANSWERS_SAVED);
          return res;
        })
    );
  }

  updatePrimaryReviewerQuestions(applicationId: number, updatedQuestions: UpdateRoleApplicationQuestion[]) {
    return RoleApplicationBusinessService._updating.run(applicationId, () =>
      firstValueFrom(this.roleApplicationAPI.updatePrimaryReviewerQuestions(applicationId, updatedQuestions))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_ANSWERS_SAVED);
          return res;
        })
    );
  }

  addCommentToRoleApplication(applicationId: number, commentText: string): Promise<RoleApplicationActivity[]> {
    return RoleApplicationBusinessService._updating.run(applicationId, () =>
      firstValueFrom(this.roleApplicationAPI.addCommentToApplication(applicationId, commentText))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_COMMENT_ADDED);
          return res;
        })
    );
  }

  getApplicationsCreatedByMyReports(): Observable<RoleApplication[]> {
    return this.roleApplicationAPI.getApplicationsCreatedByMyReports();
  }

  getRoleApplicationSettings(): Observable<RoleApplicationSettings> {
    return this.roleApplicationAPI.getRoleApplicationSettings();
  }

  getCompanyRoleApplicationSettings(): Observable<RoleApplicationSettings> {
    return this.roleApplicationAPI.getCompanyRoleApplicationSettings();
  }

  updateRoleApplicationSettings(roleApplicationSettings: RoleApplicationSettings): Promise<RoleApplicationSettings> {
    return RoleApplicationBusinessService._updatingSettings.run(SETTINGS_WRITE_KEY, () =>
      firstValueFrom(this.roleApplicationAPI.updateRoleApplicationSettings(roleApplicationSettings))
        .then(res => {
          this.notifyUtils.notify(RoleApplicationMessages.SUCCESS_SETTINGS_UPDATED);
          return res;
        })
    );
  }

  getFiltered(parentFilter: ParentFilter): Observable<Page<RoleApplication>> {
    return this.roleApplicationAPI.getFiltered(parentFilter);
  }

  getFilteredExport(parentFilter: ParentFilter): Observable<Blob> {
    return this.roleApplicationAPI.getFilteredExport(parentFilter);
  }
}